import { createRoot } from "react-dom/client";
import { Component, type ReactNode } from "react";
import { initDatadog } from "@/lib/datadog";
// Registered at module scope rather than from a component: a worker gated on a route being mounted
// leaves an installed app with no offline shell wherever it happens to launch.
import { registerServiceWorker } from "@commander4/elements/lib/service-worker";
import { initialEmbedTheme, isEmbedded } from "@commander4/elements/lib/embed-protocol";
import "./globals.css";
import "./lib/i18n";

initDatadog();

// Before first paint, so pages never flash the sidebar offset (see --app-sidebar-width) or the
// dark palette under a light host (see `:root.light` in globals.css).
if (isEmbedded()) {
  document.documentElement.dataset.embedded = "";
  if (initialEmbedTheme() === "light") document.documentElement.classList.add("light");
}

/*
 * The worker is registered BEFORE `App` is imported, and the ordering is load-bearing.
 *
 * `AuthContext` builds its OpenAuth client at module scope from `import.meta.env.VITE_AUTH_URL`, so
 * a missing or malformed value throws `Error: No issuer` while that module is still evaluating —
 * taking the whole entry module down with it. Registration placed after the import never ran: the
 * app showed its error boundary AND silently had no offline shell, so a misconfigured deploy lost
 * the cached shell that would have let the previous good build keep working.
 *
 * Verified in a browser: with `VITE_AUTH_URL` unset the page threw `No issuer` and
 * `getRegistrations()` returned 0. With the call hoisted above the import, the worker installs and
 * caches the shell regardless of what `App` does afterwards.
 *
 * A dynamic import is what makes the ordering real — a static `import App` is hoisted above
 * everything by the module system, so moving the statement down the file would change nothing.
 */
registerServiceWorker();

const App = (await import("./App.tsx")).default;

class RootErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "sans-serif", padding: "2rem", background: "#0a0a0a", color: "#e5e5e5" }}>
          <div style={{ maxWidth: 480, textAlign: "center" }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Application error</h1>
            <p style={{ color: "#888", fontSize: 13, marginBottom: 24 }}>{this.state.error.message}</p>
            <button onClick={() => window.location.reload()} style={{ padding: "8px 20px", borderRadius: 8, background: "#7c3aed", color: "#fff", border: "none", cursor: "pointer" }}>
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById("root")!).render(
  <RootErrorBoundary>
    <App />
  </RootErrorBoundary>
);
